import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import {
	Command,
	CommandEmpty,
	CommandGroup,
	CommandInput,
	CommandItem,
	CommandList,
	CommandSeparator,
} from "@/components/ui/command";
import { StructureTypeIcon } from "@/components/StructureTypeIcon";
import { structure } from "@/endpoints/dryp/structure";
import {
	MapPin,
	Building2,
	Droplets,
	AlertCircle,
	Bell,
	CloudRain,
	FolderOpen,
	Users,
	Settings,
	Loader2,
} from "lucide-react";
import { Routes } from "@/config/routes";

interface NavItem {
	label: string;
	icon: React.ReactNode;
	path: string;
	keywords: string;
}

const NAV_ITEMS: NavItem[] = [
	{
		label: "Map",
		icon: <MapPin className="mr-2 h-4 w-4" />,
		path: Routes.Dashboard.Map.path,
		keywords: "map overview",
	},
	{
		label: "Structures",
		icon: <Building2 className="mr-2 h-4 w-4" />,
		path: Routes.Dashboard.Structures.Structures.path,
		keywords: "structures list",
	},
	{
		label: "Infiltration & Inflow",
		icon: <Droplets className="mr-2 h-4 w-4" />,
		path: Routes.Dashboard.InfiltrationInflow.path,
		keywords: "infiltration inflow iandi i&i",
	},
	{
		label: "Blockages",
		icon: <AlertCircle className="mr-2 h-4 w-4" />,
		path: Routes.Dashboard.Blockages.path,
		keywords: "blockages blockage",
	},
	{
		label: "Events",
		icon: <Bell className="mr-2 h-4 w-4" />,
		path: Routes.Dashboard.Events.path,
		keywords: "events alerts",
	},
	{
		label: "Precipitation",
		icon: <CloudRain className="mr-2 h-4 w-4" />,
		path: Routes.Dashboard.Precipitation.path,
		keywords: "precipitation rain rainfall",
	},
	{
		label: "Groups",
		icon: <FolderOpen className="mr-2 h-4 w-4" />,
		path: Routes.Dashboard.Admin.Groups.path,
		keywords: "groups structure groups",
	},
	{
		label: "Users",
		icon: <Users className="mr-2 h-4 w-4" />,
		path: Routes.Dashboard.Admin.Users.path,
		keywords: "users administration",
	},
	{
		label: "Settings",
		icon: <Settings className="mr-2 h-4 w-4" />,
		path: Routes.Dashboard.Admin.Settings.path,
		keywords: "settings preferences",
	},
];

export const CommandPalette = () => {
	const [open, setOpen] = useState(false);
	const [query, setQuery] = useState("");
	const [structures, setStructures] = useState<Dryp.Structure[]>([]);
	const [groups, setGroups] = useState<Dryp.StructureGroup[]>([]);
	const [loading, setLoading] = useState(false);
	const navigate = useNavigate();
	const debounceRef = useRef<ReturnType<typeof setTimeout>>(undefined);
	const sequenceRef = useRef(0);

	// Keyboard shortcut
	useEffect(() => {
		const onKeyDown = (e: KeyboardEvent) => {
			if (e.key === "k" && (e.metaKey || e.ctrlKey)) {
				e.preventDefault();
				setOpen(prev => !prev);
			}
		};
		document.addEventListener("keydown", onKeyDown);
		return () => document.removeEventListener("keydown", onKeyDown);
	}, []);

	// Fetch groups once on first open
	useEffect(() => {
		if (!open || groups.length > 0) return;
		structure.getAllStructureGroups({ pageNumber: 0, pageSize: 200 }).then(res => {
			setGroups(res.items ?? []);
		});
	}, [open, groups.length]);

	// Reset on close
	useEffect(() => {
		if (!open) {
			setQuery("");
			setStructures([]);
			setLoading(false);
		}
	}, [open]);

	// Cleanup on unmount
	useEffect(() => {
		return () => {
			clearTimeout(debounceRef.current);
		};
	}, []);

	// Debounced structure search
	const searchStructures = useCallback((q: string) => {
		clearTimeout(debounceRef.current);
		if (q.length < 2) {
			setStructures([]);
			setLoading(false);
			return;
		}
		setLoading(true);
		debounceRef.current = setTimeout(async () => {
			const seq = ++sequenceRef.current;
			try {
				const res = await structure.getAllStructures({ pageNumber: 0, pageSize: 8 }, { search: q });
				// Discard stale responses
				if (seq !== sequenceRef.current) return;
				setStructures(res.items ?? []);
			} catch {
				// failed — ignore
			} finally {
				if (seq === sequenceRef.current) setLoading(false);
			}
		}, 300);
	}, []);

	const onQueryChange = (value: string) => {
		setQuery(value);
		if (value.length >= 2) setLoading(true);
		searchStructures(value);
	};

	const select = (path: string) => {
		setOpen(false);
		navigate(path);
	};

	// Client-side filtering for nav and groups
	const filteredNav = useMemo(() => {
		if (query.length === 0) return NAV_ITEMS;
		const q = query.toLowerCase();
		return NAV_ITEMS.filter(
			item => item.label.toLowerCase().includes(q) || item.keywords.toLowerCase().includes(q),
		);
	}, [query]);

	const filteredGroups = useMemo(() => {
		if (query.length < 2) return [];
		const q = query.toLowerCase();
		return groups.filter(g => g.name.toLowerCase().includes(q)).slice(0, 5);
	}, [query, groups]);

	const hasResults = filteredNav.length > 0 || structures.length > 0 || filteredGroups.length > 0;

	return (
		<Dialog open={open} onOpenChange={setOpen}>
			<DialogContent className="overflow-hidden p-0">
				<Command
					shouldFilter={false}
					className="[&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:font-medium [&_[cmdk-group-heading]]:text-muted-foreground [&_[cmdk-group]:not([hidden])_~[cmdk-group]]:pt-0 [&_[cmdk-group]]:px-2 [&_[cmdk-input-wrapper]_svg]:h-5 [&_[cmdk-input-wrapper]_svg]:w-5 [&_[cmdk-input]]:h-12 [&_[cmdk-item]]:px-2 [&_[cmdk-item]]:py-3 [&_[cmdk-item]_svg]:h-5 [&_[cmdk-item]_svg]:w-5"
				>
					<CommandInput
						placeholder="Search structures, groups, or jump to..."
						value={query}
						onValueChange={onQueryChange}
					/>
					<CommandList>
						{!loading && !hasResults && <CommandEmpty>No results found.</CommandEmpty>}

						{filteredNav.length > 0 && (
							<CommandGroup heading="Navigation">
								{filteredNav.map(item => (
									<CommandItem key={item.path} value={item.label} onSelect={() => select(item.path)}>
										{item.icon}
										{item.label}
									</CommandItem>
								))}
							</CommandGroup>
						)}

						{query.length >= 2 && (
							<>
								<CommandSeparator />
								<CommandGroup heading="Structures">
									{loading && (
										<div className="flex items-center justify-center py-3 gap-2">
											<Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
											<span className="text-xs text-muted-foreground">Searching...</span>
										</div>
									)}
									{structures.map(s => (
										<CommandItem
											key={s.id}
											value={s.name}
											onSelect={() =>
												select(Routes.Dashboard.Structures.Structure.build({ id: s.id }))
											}
										>
											<StructureTypeIcon typeId={s.type?.id} size={16} className="mr-2" />
											<span className="flex-1 truncate">{s.name}</span>
											{s.type?.name && (
												<span className="text-[10px] text-muted-foreground ml-2">
													{s.type.name}
												</span>
											)}
										</CommandItem>
									))}
									{!loading && structures.length === 0 && (
										<div className="py-2 px-2 text-xs text-muted-foreground">
											No structures found
										</div>
									)}
								</CommandGroup>
							</>
						)}

						{filteredGroups.length > 0 && (
							<>
								<CommandSeparator />
								<CommandGroup heading="Groups">
									{filteredGroups.map(g => (
										<CommandItem
											key={g.id}
											value={g.name}
											onSelect={() => select(Routes.Dashboard.Admin.Groups.path)}
										>
											<FolderOpen className="mr-2 h-4 w-4 text-purple-500" />
											{g.name}
										</CommandItem>
									))}
								</CommandGroup>
							</>
						)}
					</CommandList>
				</Command>
			</DialogContent>
		</Dialog>
	);
};
